/**
 * CalorieGramsRow.tsx — Phase-2 row showing only kcal/day.
 *
 * V25 (Santiago 2026-04-29): grams vary by recipe chosen (160 g to 1,180 g),
 * so showing a single average value misleads the user. Only kcal is shown
 * here; P11 displays grams per specific recipe.
 */

import React from 'react';
import { useCountUp } from './useCountUp';
import { fmt } from './ResultRow';

export interface CalorieGramsRowProps {
  visible: boolean;
  resolved: boolean;
  kcal?: number;
}

export const CalorieGramsRow: React.FC<CalorieGramsRowProps> = ({ visible, resolved, kcal }) => {
  const animatedKcal = useCountUp(kcal, resolved && visible);

  return (
    <div
      className={`flex items-center gap-3 transition-all duration-400 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      }`}
      aria-hidden={!visible}
    >
      <span
        className={`text-lg flex-shrink-0 transition-transform duration-300 ${visible ? 'scale-100' : 'scale-50'}`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.34,1.56,0.64,1)' }}
      >
        🔥
      </span>
      <span className="flex-1 text-sm font-medium">
        {resolved ? (
          <>
            <span className="text-base font-bold text-[#008BBC] tabular-nums">{fmt(animatedKcal)} kcal</span>
            <span className="text-gray-700"> al día</span>
          </>
        ) : (
          <span className="text-gray-500">
            <span className="inline-flex gap-0.5 mr-1 align-middle">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-1 h-1 bg-gray-300 rounded-full animate-bounce inline-block"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </span>
            calculando kcal diarias
          </span>
        )}
      </span>
      {resolved && <span className="text-green-500 font-bold text-sm flex-shrink-0">✓</span>}
    </div>
  );
};
